import React, { useState } from 'react';
import { MessageSquare, Zap, GraduationCap, CheckCircle2, Send, User, Mail, Phone, Briefcase } from 'lucide-react';

const EnquiryFormSection = ({ defaultCourse = 'Online MBA' }) => {
  const [enquiry, setEnquiry] = useState({
    name: '',
    email: '',
    phone: '',
    course: defaultCourse || 'Online MBA',
    experience: '1-3 Years',
    specialization: 'Finance',
    message: '',
    consent: true
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEnquiry((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!enquiry.name || !enquiry.email || !enquiry.phone || !enquiry.consent) {
      alert('Please complete all required fields.');
      return;
    }
    setSubmitted(true);
  };

  return (
    <section className="enquiry-section" id="enquiry-section">
      <div className="enquiry-container">
        <div className="enquiry-grid">
          <div className="enquiry-info">
            <span className="enquiry-badge">GET IN TOUCH</span>
            <h2 className="enquiry-title">
              Have Questions? Talk to Our <span>Academic Advisors</span>
            </h2>
            <p className="enquiry-description">
              Whether you need details about course fee structures, EMI options, eligibility criteria, or career placement support, our expert counseling team is here to guide you every step of the way.
            </p>

            <div className="enquiry-highlights">
              <div className="highlight-item">
                <div className="highlight-icon">
                  <MessageSquare size={22} color="#FF6B00" />
                </div>
                <div>
                  <h4>1-on-1 Counseling</h4>
                  <p>Get personalized guidance tailored to your career aspirations.</p>
                </div>
              </div>
              <div className="highlight-item">
                <div className="highlight-icon">
                  <Zap size={22} color="#FF6B00" />
                </div>
                <div>
                  <h4>Quick Assistance</h4>
                  <p>Receive immediate responses via phone call or WhatsApp.</p>
                </div>
              </div>
              <div className="highlight-item">
                <div className="highlight-icon">
                  <GraduationCap size={22} color="#FF6B00" />
                </div>
                <div>
                  <h4>Scholarship Eligibility Check</h4>
                  <p>Find out your instant eligibility for up to 15% fee scholarship.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="enquiry-form-card">
            {submitted ? (
              <div className="enquiry-success-box">
                <div className="success-badge">
                  <CheckCircle2 size={40} color="#10b981" />
                </div>
                <h3>Enquiry Submitted Successfully!</h3>
                <p>
                  Thank you <strong>{enquiry.name}</strong>. An academic advisor for{' '}
                  <strong>{enquiry.course}</strong> will call you at{' '}
                  <strong>+91 {enquiry.phone}</strong> shortly.
                </p>
                <button
                  className="btn-secondary"
                  onClick={() => setSubmitted(false)}
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="enquiry-form">
                <h3 className="form-title">Enquiry Form</h3>

                <div className="form-row">
                  <div className="form-group icon-input-group">
                    <label>Full Name *</label>
                    <div className="input-with-icon">
                      <User size={16} className="field-icon" />
                      <input
                        type="text"
                        name="name"
                        placeholder="e.g. Rahul Sharma"
                        value={enquiry.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group icon-input-group">
                    <label>Email Address *</label>
                    <div className="input-with-icon">
                      <Mail size={16} className="field-icon" />
                      <input
                        type="email"
                        name="email"
                        placeholder="e.g. rahul@example.com"
                        value={enquiry.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group icon-input-group">
                    <label>Phone Number *</label>
                    <div className="input-with-icon">
                      <Phone size={16} className="field-icon" />
                      <input
                        type="tel"
                        name="phone"
                        placeholder="10-digit mobile number"
                        value={enquiry.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group icon-input-group">
                    <label>Select Course *</label>
                    <div className="input-with-icon">
                      <GraduationCap size={16} className="field-icon" />
                      <select
                        name="course"
                        value={enquiry.course || 'Online MBA'}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Course *</option>
                        <option value="Online MBA">Online MBA (Master of Business Administration)</option>
                        <option value="Online MCA">Online MCA (Master of Computer Applications)</option>
                        <option value="Online BBA">Online BBA (Bachelor of Business Administration)</option>
                        <option value="Online BCA">Online BCA (Bachelor of Computer Applications)</option>
                        <option value="Online B.Com">Online B.Com (Bachelor of Commerce)</option>
                        <option value="Online M.Com">Online M.Com (Master of Commerce)</option>
                        <option value="Online MSc Data Science">Online MSc (Data Science)</option>
                        <option value="Online MA JMC">Online MA (Journalism & Mass Comm)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group icon-input-group">
                    <label>Work Experience</label>
                    <div className="input-with-icon">
                      <Briefcase size={16} className="field-icon" />
                      <select
                        name="experience"
                        value={enquiry.experience}
                        onChange={handleChange}
                      >
                        <option value="Fresher">Fresher / Student (0 Yrs)</option>
                        <option value="1-3 Years">1 - 3 Years</option>
                        <option value="3-5 Years">3 - 5 Years</option>
                        <option value="5+ Years">5+ Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Preferred Specialization</label>
                    <select
                      name="specialization"
                      value={enquiry.specialization}
                      onChange={handleChange}
                    >
                      <option value="Finance">Finance</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Human Resource">Human Resource Management</option>
                      <option value="Business Analytics">Business Analytics</option>
                      <option value="Operations">Operations & Supply Chain</option>
                      <option value="Healthcare">Healthcare Management</option>
                      <option value="IT Systems">IT & Systems</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Your Message / Specific Query (Optional)</label>
                  <textarea
                    name="message"
                    rows="3"
                    placeholder="Ask about fee breakdown, eligibility, syllabus, etc."
                    value={enquiry.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={enquiry.consent}
                      onChange={handleChange}
                      required
                    />
                    <span>
                      I authorize Online Manipal to reach out to me via SMS/Email/WhatsApp regarding my MBA enquiry.
                    </span>
                  </label>
                </div>

                <button type="submit" className="btn-primary submit-enquiry-btn">
                  <Send size={18} style={{ marginRight: '6px' }} /> Submit Enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnquiryFormSection;
